import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Icon, Text } from '@msc-b2c/shared-components';
import { DiningAvailabilityError } from '@msc-b2c/interfaces/enums';
import {
  DateSelectionStepProps,
  ItineraryDay,
  WeekGroup,
  WeekFilterButtonProps,
  DayButtonProps,
} from '@msc-b2c/interfaces/interfaces';
import styles from './dateSelectionStep.module.scss';
import { isLongCruise, groupDaysByWeek } from '../../utils/diningDateUtils';
import { hasAvailabilityInDateSet } from '../../utils/diningAvailabilityUtils';
import { TimeSelectionStep } from '../TimeSelectionStep/TimeSelectionStep';
import { FlowSelectableCard } from '../../components/FlowSelectableCard/FlowSelectableCard';
import clsx from 'clsx';
import { useHorizontalOverflow } from '../../hooks/useHorizontalOverflow';
import { useFrozenDateSelectionUi } from '../../hooks/useFrozenDateSelectionUi';

const WeekFilterButton: React.FC<WeekFilterButtonProps> = ({
  weekGroup,
  isSelected,
  hasAvailability,
  onClick,
  isDisabledDueToConfirming,
}) => (
  <FlowSelectableCard
    primaryText={weekGroup.label}
    isSelected={isSelected}
    isDisabled={!hasAvailability || isDisabledDueToConfirming}
    onClick={onClick}
  />
);

const DayButton: React.FC<DayButtonProps> = ({
  day,
  dayLabel,
  dayAtSeaLabel,
  unavailabilityLabel,
  isAvailable,
  isSelected,
  onClick,
  icon,
  isDisabledDueToConfirming,
}) => {
  // Split day number from port/sea label
  const dayNumberText = `${dayLabel} ${day.dayNumber}`;
  const portOrSeaText = day.isAtSea ? dayAtSeaLabel : day.portName || dayAtSeaLabel;

  return (
    <FlowSelectableCard
      primaryText={dayNumberText}
      secondaryText={portOrSeaText}
      badgeText={!isAvailable ? unavailabilityLabel : undefined}
      isSelected={isSelected}
      isDisabled={!isAvailable || isDisabledDueToConfirming}
      onClick={onClick}
      icon={icon}
    />
  );
};

const SKELETON_ROWS = 3;

/**
 * DateSelectionStep Component
 *
 *
 *   - Week filter buttons (Days 1-7, Days 8-14, etc.)
 *   - Day buttons grouped under active week
 *   - Disabled/greyed-out if no availability
 *
 * AC2 (Cruise <= 7 days):
 *   - Day buttons displayed directly (no week filters)
 *   - Disabled/greyed-out if no availability
 *
 * AC3:
 *   - When a day is selected, callback fires
 *   - Skeleton loader shown while fetching details
 */
export const DateSelectionStep: React.FC<DateSelectionStepProps> = ({
  itineraryDays,
  availabilitiesByDate,
  diningDateTimeProps,
  currentReservationIndex = 1,
  maximumSlotsBookable = 1,
  restaurantOptions,
  selectedRestaurantCode,
  selectedDate,
  onSelectDay,
  selectedTime,
  onSelectTime,
  onSelectRestaurantTime,
  onConflictRestaurantTimeSelect,
  timeslots,
  restaurantCode,
  restaurantName,
  isLoading,
  availabilityError,
  onConfirmSelection,
  isConfirming,
  isCommittingSelection = false,
  loading = false,
  hideConfirmSelectionCta,
  selectedDays = [],
  editingReservationDayNumber,
  isProductEntryPoint = false,
  sortedIndexToOriginalIndex,
}) => {
  const filtersContainerRef = useRef<HTMLDivElement>(null);
  const { hasOverflow } = useHorizontalOverflow(filtersContainerRef);
  const isInteractionLocked = Boolean(isConfirming || isCommittingSelection);

  // Track initial selections on mount to detect user changes
  const initialSelectionsRef = useRef<{
    readonly date: string | null;
    readonly restaurantCode: string | null;
    readonly time: string | null;
  } | null>(null);

  useEffect(() => {
    if (initialSelectionsRef.current === null) {
      initialSelectionsRef.current = {
        date: selectedDate || null,
        restaurantCode: selectedRestaurantCode || null,
        time: selectedTime || null,
      };
    }
  }, []);

  const {
    effectiveSelectedDate,
    effectiveSelectedRestaurantCode,
    effectiveSelectedTime,
    effectiveRestaurantOptions,
    effectiveTimeslots,
    effectiveSelectedDays,
  } = useFrozenDateSelectionUi({
    isInteractionLocked,
    selectedDate,
    selectedRestaurantCode,
    selectedTime,
    restaurantOptions,
    timeslots,
    selectedDays,
  });

  // Determine if user has changed their selection from initial state
  const hasUserChangedSelection =
    initialSelectionsRef.current &&
    (selectedDate !== initialSelectionsRef.current.date ||
      selectedRestaurantCode !== initialSelectionsRef.current.restaurantCode ||
      selectedTime !== initialSelectionsRef.current.time);

  const cruiseDayCount = itineraryDays.length;
  const isLong = isLongCruise(cruiseDayCount);
  const lastCruiseDayDate = itineraryDays[itineraryDays.length - 1]?.date;

  // Group days by weeks for long cruises
  const weekGroups = useMemo(
    () => (isLong ? groupDaysByWeek(itineraryDays, diningDateTimeProps?.weekFilterLabel) : []),
    [isLong, itineraryDays, diningDateTimeProps?.weekFilterLabel]
  );

  // Track selected week (for filtering day display in AC1)
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(0);

  const reservationsCountByDay = useMemo(() => {
    return effectiveSelectedDays.reduce<Map<number, number>>((acc, dayNumber) => {
      acc.set(dayNumber, (acc.get(dayNumber) ?? 0) + 1);
      return acc;
    }, new Map<number, number>());
  }, [effectiveSelectedDays]);

  const selectedDayNumber = useMemo(() => {
    if (!effectiveSelectedDate) {
      return null;
    }
    return itineraryDays.find((day) => day.date === effectiveSelectedDate)?.dayNumber ?? null;
  }, [effectiveSelectedDate, itineraryDays]);

  const selectedDayIncludedInReservations = useMemo(() => {
    const editedDayNumber = editingReservationDayNumber ?? selectedDayNumber;
    if (editedDayNumber === null || editedDayNumber === undefined) {
      return false;
    }
    return effectiveSelectedDays.includes(editedDayNumber);
  }, [editingReservationDayNumber, selectedDayNumber, effectiveSelectedDays]);

  // Get days to display based on view mode
  const displayDays = useMemo(() => {
    if (!isLong) {
      // AC2: Show all days
      return itineraryDays;
    }

    // AC1: Show days from selected week only
    if (weekGroups.length > 0) {
      return weekGroups[selectedWeekIndex]?.days || [];
    }

    return [];
  }, [isLong, itineraryDays, weekGroups, selectedWeekIndex]);

  // Enrich week groups with availability info
  const enrichedWeeks = useMemo(() => {
    return weekGroups.map((week: WeekGroup) => {
      const dates = week.days.map((d: ItineraryDay) => d.date);
      const hasAvailability = hasAvailabilityInDateSet(dates, availabilitiesByDate);

      return { ...week, hasAvailability };
    });
  }, [weekGroups, availabilitiesByDate]);

  // Long cruise + pre-filled date: show the week that contains that day (before paint).
  useLayoutEffect(() => {
    if (isInteractionLocked || !isLong || !effectiveSelectedDate || weekGroups.length === 0) {
      return;
    }
    const weekIdx = weekGroups.findIndex((w) =>
      w.days.some((d) => d.date === effectiveSelectedDate)
    );
    if (weekIdx >= 0 && weekIdx !== selectedWeekIndex) {
      setSelectedWeekIndex(weekIdx);
    }
  }, [isInteractionLocked, isLong, effectiveSelectedDate, weekGroups, selectedWeekIndex]);

  // First *enabled* week filter is selected by default (not a disabled week 1–7).
  useEffect(() => {
    if (isInteractionLocked || !isLong || enrichedWeeks.length === 0 || effectiveSelectedDate) {
      return;
    }
    const firstEnabledIdx = enrichedWeeks.findIndex((w) => w.hasAvailability);
    if (firstEnabledIdx === -1) {
      return;
    }
    const currentHasAvailability = enrichedWeeks[selectedWeekIndex]?.hasAvailability ?? false;
    if (!currentHasAvailability) {
      setSelectedWeekIndex(firstEnabledIdx);
    }
  }, [isInteractionLocked, isLong, enrichedWeeks, effectiveSelectedDate, selectedWeekIndex]);

  // Changing week clears the day only if that day is not in the newly visible week.
  useEffect(() => {
    if (isInteractionLocked || !isLong || weekGroups.length === 0) {
      return;
    }
    const daysInWeek = weekGroups[selectedWeekIndex]?.days ?? [];
    if (effectiveSelectedDate && !daysInWeek.some((d) => d.date === effectiveSelectedDate)) {
      onSelectDay('', 0);
    }
  }, [
    isInteractionLocked,
    isLong,
    onSelectDay,
    effectiveSelectedDate,
    selectedWeekIndex,
    weekGroups,
  ]);

  const requiresRestaurantSelection = (restaurantOptions?.length ?? 0) > 0;
  const isConfirmSelectionEnabled =
    !!effectiveSelectedTime && (!requiresRestaurantSelection || !!effectiveSelectedRestaurantCode);
  const confirmSelectionLabel = diningDateTimeProps?.confirmSelectionLabel;
  const confirmSelectionLoadingLabel = diningDateTimeProps?.confirmSelectionLoadingLabel;
  const shouldShowRestaurantListSkeleton = false;
  const reservationCounterTemplate = diningDateTimeProps?.reservationCounterLabel || '';

  const retrieveReservationIndex = (index?: number) => {
    if (index === undefined || index === null || sortedIndexToOriginalIndex === undefined) {
      return currentReservationIndex;
    }
    const i = sortedIndexToOriginalIndex.get(index - 1);
    return i !== undefined ? i + 1 : currentReservationIndex;
  };

  const reservationCounterLabel = reservationCounterTemplate
    .replace('{currentReservationIndex}', String(retrieveReservationIndex(currentReservationIndex)))
    .replace('{maximumSlotsBookable}', String(maximumSlotsBookable));

  if (availabilityError) {
    const errorText =
      availabilityError === DiningAvailabilityError.PER_PACKAGE_LIMIT
        ? diningDateTimeProps?.maximumReservationsReachedPerPackageLabel
        : diningDateTimeProps?.maximumReservationsReachedForRestaurantLabel;
    return (
      <div className={styles['date-selection__error']}>
        {errorText && (
          <Text
            text={errorText}
            styleOption="body-default"
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(styles['date-selection'], {
        [styles['-loading']]: isLoading,
        [styles['-confirming']]: isInteractionLocked,
      })}
    >
      {maximumSlotsBookable > 1 && (
        <div className={styles['date-selection__reservation']}>
          {!!diningDateTimeProps?.reservationCounterTitle && (
            <div className={styles['date-selection__reservation-text']}>
              {!!diningDateTimeProps?.reservationCounterIcon && (
                <div className={styles['date-selection__reservation-icon']}>
                  <Icon icon={diningDateTimeProps.reservationCounterIcon} />
                </div>
              )}
              <div className={styles['date-selection__reservation-title']}>
                <Text
                  text={diningDateTimeProps.reservationCounterTitle}
                  styleOption="cmp-text__big"
                />
              </div>
            </div>
          )}
          <div className={styles['date-selection__reservation-counter']}>
            <Text
              text={reservationCounterLabel}
              styleOption="cmp-text__big"
            />
          </div>
        </div>
      )}

      {/* Subheading */}
      <div className={styles['date-selection__header']}>
        <Text
          text={diningDateTimeProps?.makeReservationTitle}
          styleOption="header-xs"
        />
        {/* Select a day instruction */}
        <div className={styles['date-selection__instruction']}>
          <Text
            text={diningDateTimeProps?.selectDayLabel}
            styleOption="body-default"
          />
        </div>
      </div>

      {/* AC1: Week filter buttons (only for long cruises) */}
      {isLong && enrichedWeeks.length > 0 && (
        <div className={styles['date-selection__week-filters-wrapper']}>
          {!!diningDateTimeProps?.weekFiltersLabel && (
            <div className={styles['date-selection__week-filters-title']}>
              <Text
                text={diningDateTimeProps.weekFiltersLabel}
                styleOption="body-small"
              />
            </div>
          )}
          <div
            className={clsx(styles['date-selection__week-filters'], {
              [styles['-with-gradient']]: hasOverflow,
            })}
          >
            <div
              ref={filtersContainerRef}
              className={styles['date-selection__week-filters-buttons']}
            >
              {enrichedWeeks.map((week: WeekGroup & { hasAvailability?: boolean }, idx: number) => (
                <WeekFilterButton
                  key={`week-${week.weekNumber}`}
                  weekGroup={week}
                  isSelected={idx === selectedWeekIndex}
                  hasAvailability={week.hasAvailability ?? false}
                  isDisabledDueToConfirming={isInteractionLocked}
                  onClick={() => {
                    if (isInteractionLocked) {
                      return;
                    }
                    onSelectDay('', 0);
                    setSelectedWeekIndex(idx);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AC1 & AC2: Day buttons grid */}
      <div className={styles['date-selection__days-grid']}>
        {displayDays.map((day: ItineraryDay) => {
          const dateSlots = availabilitiesByDate.get(day.date);
          const emptySlots = (dateSlots?.totalSlots ?? 0) === 0;
          const isAvailable = !emptySlots && (dateSlots?.hasAvailableSlots ?? false);
          const isSelected = effectiveSelectedDate === day.date;
          const totalReservationsOnDay = reservationsCountByDay.get(day.dayNumber) ?? 0;
          const editedDayNumber = editingReservationDayNumber ?? selectedDayNumber;
          const reservationsOnDayExcludingEdited =
            selectedDayIncludedInReservations && editedDayNumber === day.dayNumber
              ? Math.max(totalReservationsOnDay - 1, 0)
              : totalReservationsOnDay;
          const hasOtherReservations = reservationsOnDayExcludingEdited > 0;
          const icon = hasOtherReservations
            ? isSelected
              ? diningDateTimeProps.filledDayIconActive
              : diningDateTimeProps.filledDayIcon
            : '';
          const isLastCruiseDay = day.date === lastCruiseDayDate;
          let unavailabilityLabel = '';
          if (!isLoading) {
            if (isLastCruiseDay) {
              unavailabilityLabel = diningDateTimeProps?.unavailableLabel ?? '';
            } else if (emptySlots) {
              unavailabilityLabel = diningDateTimeProps?.noTimeslotsLabel ?? '';
            } else {
              unavailabilityLabel = diningDateTimeProps?.fullyBookedLabel ?? '';
            }
          }

          return (
            <DayButton
              key={`day-${day.dayNumber}-${day.date}`}
              day={day}
              dayLabel={diningDateTimeProps?.dayLabel}
              dayAtSeaLabel={diningDateTimeProps?.dayAtSeaLabel}
              unavailabilityLabel={unavailabilityLabel}
              isAvailable={isAvailable}
              isSelected={isSelected}
              icon={icon}
              isDisabledDueToConfirming={isInteractionLocked}
              onClick={() => {
                if (isInteractionLocked) {
                  return;
                }
                onSelectDay(day.date, day.dayNumber);
              }}
            />
          );
        })}
      </div>

      {/* AC3: TimeSelectionStep shown below when a date is selected */}
      {effectiveSelectedDate && onSelectTime && !shouldShowRestaurantListSkeleton && (
        <div className={styles['date-selection__timeslots-section']}>
          {(effectiveRestaurantOptions && effectiveRestaurantOptions.length > 0
            ? effectiveRestaurantOptions
            : [
                {
                  code: restaurantCode ?? '',
                  name: restaurantName,
                  timeslots: effectiveTimeslots ?? [],
                },
              ]
          ).map((restaurant) => (
            <div
              className={styles['date-selection__restaurant-slot']}
              key={`restaurant-${restaurant.code}`}
            >
              <TimeSelectionStep
                restaurantCode={restaurant.code}
                restaurantName={restaurant.name}
                restaurantCategory={restaurant.category}
                diningDateTimeProps={diningDateTimeProps}
                timeslots={restaurant.timeslots}
                restaurantLimit={restaurant.maximumBookingLimit}
                isLimitReached={restaurant.isLimitReached}
                conflictTimes={restaurant.conflictTimes}
                selectedTime={
                  effectiveSelectedRestaurantCode === restaurant.code
                    ? effectiveSelectedTime
                    : undefined
                }
                isSingleSlotLayout={maximumSlotsBookable === 1}
                isInteractionLocked={isInteractionLocked}
                onSelectTime={(time) => {
                  if (isInteractionLocked) {
                    return;
                  }
                  if (onSelectRestaurantTime) {
                    onSelectRestaurantTime(restaurant.code, time);
                    return;
                  }
                  onSelectTime(time);
                }}
                onConflictTimeSelect={(time) => {
                  if (isInteractionLocked) {
                    return;
                  }
                  if (onSelectRestaurantTime) {
                    onSelectRestaurantTime(restaurant.code, '');
                  } else {
                    onSelectTime('');
                  }
                  onConflictRestaurantTimeSelect?.(restaurant.code, time);
                }}
                isLoading={isLoading}
                availabilityError={
                  selectedRestaurantCode === restaurant.code ? availabilityError : undefined
                }
                totalRestaurantsCount={effectiveRestaurantOptions?.length ?? 1}
              />

              {effectiveSelectedDate &&
                effectiveSelectedRestaurantCode === restaurant.code &&
                isConfirmSelectionEnabled &&
                !hideConfirmSelectionCta &&
                (hasUserChangedSelection || (isProductEntryPoint && !restaurant.isLimitReached)) &&
                onConfirmSelection && (
                  <div className={styles['date-selection__confirm-cta']}>
                    <Button
                      text={isConfirming ? confirmSelectionLoadingLabel : confirmSelectionLabel}
                      icon={
                        isConfirming
                          ? diningDateTimeProps?.confirmSelectionLoadingLabelIcon
                          : undefined
                      }
                      disabled={isInteractionLocked}
                      onClick={onConfirmSelection}
                      styleOption="cmp-button__primary -extended"
                    />
                  </div>
                )}
            </div>
          ))}
        </div>
      )}

      {shouldShowRestaurantListSkeleton && (
        <div
          className={styles['date-selection__skeleton-list']}
          data-testid="restaurant-list-skeleton"
        >
          {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <div
              key={`skeleton-row-${index}`}
              className={styles['date-selection__skeleton-row']}
              data-testid="restaurant-list-skeleton-row"
            >
              <div className={styles['date-selection__skeleton-image']} />
              <div className={styles['date-selection__skeleton-copy']}>
                <div className={styles['date-selection__skeleton-line']} />
                <div className={styles['date-selection__skeleton-line']} />
                <div className={styles['date-selection__skeleton-line']} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DateSelectionStep;
